import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
    Banner,
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Loading,
    Page,
    TextField,
} from "@shopify/polaris";
import { useStore } from "../../app/stores/store";
import { UserFormValues } from "../../app/models/User";
import { getShopifyAuthFromSearch, stripShopifyAuthParams } from "../../app/utils/shopifyAuth";

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const LoginPage = () => {
    const { userStore, commonStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [storeDomain, setStoreDomain] = useState("");
    const [authParamsPresent, setAuthParamsPresent] = useState(false);
    const [authError, setAuthError] = useState("");
    const [authAttempt, setAuthAttempt] = useState(0);
    const authentication = useRef<{ key: string; promise: Promise<void> }>();

    useEffect(() => {
        // App can remount this route after login succeeds, before the redirect.
        // The authenticated session already contains the returned user/token.
        if (userStore.isLoggedIn) return;

        const authParams = getShopifyAuthFromSearch(`?${searchParams.toString()}`);
        const hasAuthorizationCode = Boolean(authParams.shop && authParams.code && authParams.state);
        const hasEmbeddedIdToken = Boolean(authParams.shop && authParams.embedded && authParams.id_token);
        const isManagedEmbeddedLaunch = Boolean(authParams.shop && (authParams.embedded || authParams.host)) || commonStore.shopifySession;

        if (hasAuthorizationCode || hasEmbeddedIdToken || isManagedEmbeddedLaunch) {
            let cancelled = false;
            setAuthError("");
            setAuthParamsPresent(true);

            const authenticateUser = async () => {
                if (authParams.code && authParams.state) {
                    await userStore.login(authParams);
                } else if (isManagedEmbeddedLaunch) {
                    // Launch URL tokens expire after one minute. Ask App Bridge for
                    // a fresh token on every attempt, including a merchant's retry.
                    const idToken = window.shopify?.idToken
                        ? await window.shopify.idToken()
                        : authParams.id_token;

                    if (!idToken) {
                        throw new Error("Shopify did not provide an embedded app ID token.");
                    }

                    await userStore.embeddedLogin({
                        ...authParams,
                        embedded: authParams.embedded || "1",
                        id_token: idToken,
                    });
                } else {
                    throw new Error("Missing Shopify embedded app launch parameters.");
                }
            };

            const key = `${searchParams.toString()}:${authAttempt}`;
            if (authentication.current?.key !== key) {
                authentication.current = { key, promise: authenticateUser() };
            }
            void authentication.current.promise.catch(() => {
                if (!cancelled) {
                    setAuthError("Unable to authenticate the embedded Shopify app session. Try again to start a new session. If this continues, close this app tab and reopen the app from Shopify Admin.");
                    setAuthParamsPresent(false);
                }
            });
            return () => { cancelled = true; };
        }
    }, [searchParams, userStore, userStore.isLoggedIn, commonStore.shopifySession, authAttempt]);

    useEffect(() => {
        if (!userStore.isLoggedIn) {
            return;
        }

        const stateFrom = (location.state as { from?: Location } | null)?.from;
        const currentRouteIsLogin = location.pathname === "/login" || location.pathname === "/";
        const fromPath = stateFrom?.pathname || (currentRouteIsLogin ? "/dashboard" : location.pathname);
        const rawFromSearch = stateFrom?.search || (currentRouteIsLogin ? "" : location.search);
        const fromSearch = stripShopifyAuthParams(rawFromSearch);
        const fromHash = stateFrom?.hash || (currentRouteIsLogin ? "" : location.hash);
        navigate(`${fromPath}${fromSearch}${fromHash || ""}`, { replace: true });
    }, [location.hash, location.pathname, location.search, location.state, navigate, userStore.isLoggedIn]);

    const handleSubmit = () => {
        userStore.authenticate({ shopDomain: storeDomain } as UserFormValues);
    };

    const handleShopDomainChange = useCallback((value: string) => {
        setStoreDomain(value);
    }, []);

    if (authParamsPresent) {
        return (
            <div style={{ height: "100px" }}>
                <Frame>
                    <Loading />
                </Frame>
            </div>
        );
    }

    if (authError) {
        return (
            <Page title="Login">
                <Banner tone="critical" title="Shopify app session could not be started">
                    <p>{authError}</p>
                    <Button onClick={() => setAuthAttempt(attempt => attempt + 1)}>Try again</Button>
                </Banner>
            </Page>
        );
    }

    return (
        <Page title="Login">
            <Layout>
            </Layout>
        </Page>
    );
};

export default observer(LoginPage);
